import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import axios from 'axios';
import { Save, Store, MessageSquare, Loader2, Camera, Image as ImageIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css'; 

const Settings = () => {
  const [formData, setFormData] = useState({ 
    shopName: '', 
    whatsapp: '', 
    customMessage: '', 
    shopLogo: '', 
    coverPhoto: '' // Database field name match kar diya
  });
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // Load User Settings
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await api.get('/auth/user');
        setFormData({
          shopName: res.data.shopName || '',
          whatsapp: res.data.whatsapp || '', 
          customMessage: res.data.customMessage || '',
          shopLogo: res.data.shopLogo || '',
          coverPhoto: res.data.coverPhoto || ''
        });
      } catch (err) {
        toast.error("Failed to load settings");
      } finally {
        setInitialLoading(false);
      }
    };
    fetchUser();
  }, []);

  // Generic Image Upload to Cloudinary
  const uploadToCloudinary = async (file, type) => {
    const data = new FormData();
    data.append('file', file);
    data.append('upload_preset', "whatsappCatelog");

    try {
      const res = await axios.post(`https://api.cloudinary.com/v1_1/dm06twdcj/image/upload`, data);
      return res.data.secure_url;
    } catch (err) {
      throw new Error(`${type} upload failed!`);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingLogo(true);
    const uploadToast = toast.loading("Uploading logo...");
    try {
      const url = await uploadToCloudinary(file, "Logo");
      setFormData(prev => ({ ...prev, shopLogo: url }));
      toast.success("Logo updated!", { id: uploadToast });
    } catch (err) {
      toast.error(err.message, { id: uploadToast });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingCover(true);
    const uploadToast = toast.loading("Uploading cover photo...");
    try {
      const url = await uploadToCloudinary(file, "Cover Photo");
      setFormData(prev => ({ ...prev, coverPhoto: url }));
      toast.success("Cover updated!", { id: uploadToast });
    } catch (err) {
      toast.error(err.message, { id: uploadToast });
    } finally {
      setUploadingCover(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const saveToast = toast.loading("Saving settings...");
    const cleanWhatsApp = formData.whatsapp.replace(/\D/g, ''); 

    try {
      await api.put('/auth/settings', { ...formData, whatsapp: cleanWhatsApp });
      toast.success("Settings updated! 🚀", { id: saveToast });
    } catch (err) { 
      toast.error(err.response?.data?.msg || "Failed to update settings", { id: saveToast }); 
    } finally { 
      setLoading(false); 
    }
  };

  if (initialLoading) return (
    <div className="flex flex-col items-center justify-center h-64">
      <Loader2 className="animate-spin text-green-600" size={40} />
      <p className="mt-4 text-gray-500 font-bold italic text-sm uppercase tracking-widest">Loading Settings</p>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto pb-20 px-4 sm:px-0 animate-in fade-in duration-500">
      <div className="mb-8">
        <h2 className="text-3xl font-black text-gray-900 tracking-tight">Shop Settings</h2>
        <p className="text-gray-500 font-medium italic text-sm">Design your store's brand and contact information.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8 bg-white pb-10 rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden">
        
        {/* Header Images Section (Cover + Logo Overlap) */}
        <div className="relative">
          {/* Cover Photo */}
          <div className="h-44 w-full bg-gray-100 relative group overflow-hidden">
            {formData.coverPhoto ? (
              <img src={formData.coverPhoto} className="w-full h-full object-cover" alt="Cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-300">
                <ImageIcon size={40} />
              </div>
            )}
            
            <label className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 flex items-center justify-center cursor-pointer transition-opacity">
              <div className="bg-white/90 p-3 rounded-xl flex items-center gap-2 font-bold text-xs shadow-xl">
                <Camera size={16} /> Change Cover
              </div>
              <input type="file" className="hidden" onChange={handleCoverUpload} accept="image/*" />
            </label>

            {uploadingCover && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <Loader2 className="animate-spin text-white" />
              </div>
            )}
          </div>

          {/* Overlapping Logo */}
          <div className="absolute -bottom-12 left-1/2 -translate-x-1/2">
            <div className="relative group">
              <div className="w-28 h-28 rounded-full border-4 border-white overflow-hidden bg-white shadow-lg">
                {formData.shopLogo ? (
                  <img src={formData.shopLogo} className="w-full h-full object-cover" alt="Logo" />
                ) : (
                  <Store className="w-full h-full p-6 text-gray-200" />
                )}
                {uploadingLogo && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <Loader2 className="animate-spin text-white" size={20} />
                  </div>
                )}
              </div>
              <label className="absolute bottom-0 right-0 bg-green-600 text-white p-2 rounded-full cursor-pointer shadow-lg hover:bg-black transition-colors z-30">
                <Camera size={14} />
                <input type="file" className="hidden" onChange={handleLogoUpload} accept="image/*" />
              </label>
            </div>
          </div>
        </div>

        {/* Spacing for Overlap */}
        <div className="pt-12 px-8 md:px-10 space-y-6">
          
          <div className="text-center pb-4">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Store Identity</p>
          </div>

          <div className="space-y-6">
            {/* Shop Name */}
            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase ml-2 tracking-widest">Store Name</label>
              <div className="relative">
                <Store className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 z-10" size={18} />
                <input 
                  required
                  value={formData.shopName} 
                  onChange={e => setFormData({...formData, shopName: e.target.value})} 
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 rounded-2xl outline-none border border-transparent focus:border-green-500 focus:bg-white font-bold text-gray-800 transition-all"
                  placeholder="e.g. Urban Style Store" 
                />
              </div>
            </div>

            {/* WhatsApp Number */}
            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase ml-2 tracking-widest">WhatsApp Number</label>
              <div className="phone-input-container">
                <PhoneInput
                  country={'pk'}
                  value={formData.whatsapp}
                  onChange={phone => setFormData({...formData, whatsapp: phone})}
                  containerClass="!w-full"
                  inputClass="!w-full !h-[60px] !bg-gray-50 !rounded-2xl !border-none !font-bold !text-gray-800 !pl-[60px] focus:!bg-white focus:!ring-2 focus:!ring-green-500"
                  buttonClass="!bg-transparent !border-none !rounded-l-2xl !pl-4"
                  dropdownClass="!rounded-xl !shadow-xl"
                  enableSearch={true}
                />
              </div>
            </div>

            {/* Custom Message */}
            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase ml-2 tracking-widest">Checkout Note</label>
              <div className="relative">
                <MessageSquare className="absolute left-4 top-4 text-gray-300" size={18} />
                <textarea 
                  value={formData.customMessage} 
                  onChange={e => setFormData({...formData, customMessage: e.target.value})} 
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 rounded-2xl outline-none border border-transparent focus:border-green-500 focus:bg-white font-bold text-gray-800 transition-all h-28 resize-none" 
                  placeholder="Example: Near-by landmark..." 
                />
              </div>
            </div>
          </div>

          <button 
            disabled={loading || uploadingLogo || uploadingCover} 
            className="w-full py-5 bg-black text-white rounded-[1.5rem] font-black text-lg flex items-center justify-center gap-3 hover:bg-gray-800 active:scale-95 transition-all shadow-xl shadow-gray-100 disabled:bg-gray-200"
          >
            {loading ? <Loader2 className="animate-spin" /> : <Save size={22} />}
            Update All Settings
          </button>
        </div>
      </form>

      <style>{`
        .phone-input-container .form-control:focus {
            box-shadow: none !important;
            border: 2px solid #22c55e !important;
        }
        .react-tel-input .selected-flag:hover, .react-tel-input .selected-flag:focus {
            background-color: transparent !important;
        }
      `}</style>
    </div>
  );
};

export default Settings;